import { useEffect, useRef } from 'react';

const LOCATE_BTN_CLASS = 'ag-grid-locate-column-btn';
const TOOLTIP_CLASS = 'ag-grid-locate-tooltip';
const FLASH_CLASS = 'ag-grid-column-locate-flash';
const ROW_SELECTOR = '.ag-column-select-column';
const LABEL_SELECTOR = '.ag-column-select-column-label';
const FLASH_DURATION = 1400;

// Crosshair / "locate" icon injected next to each column row in the columns tool panel.
const LOCATE_ICON_SVG = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="7"></circle><line x1="12" y1="1" x2="12" y2="4"></line><line x1="12" y1="20" x2="12" y2="23"></line><line x1="1" y1="12" x2="4" y2="12"></line><line x1="20" y1="12" x2="23" y2="12"></line></svg>`;

/**
 * Adds a hover "locate" affordance to each row of the native AG Grid columns tool panel.
 * Clicking the icon horizontally scrolls the grid to that column and briefly flashes/underlines
 * its header (and visible cells), without altering the native single-click show/hide behavior.
 *
 * The tool panel rows are virtualized (recycled on scroll/search), so the row->column mapping is
 * resolved at click time by display name. Duplicate display names resolve to the first (preferably
 * visible) match — never to an unrelated column.
 *
 * @param onColumnRevealed optional callback invoked (with the api) after a hidden column is
 *        revealed, so the reveal can be persisted consistently with the native panel toggle.
 */
export const useColumnPanelLocate = (api, gridWrapperRef, { t, onColumnRevealed } = {}) => {
  // Read via refs so language/persistence-identity changes don't tear down and rebuild all the DOM.
  const tRef = useRef(t);
  tRef.current = t;
  const onColumnRevealedRef = useRef(onColumnRevealed);
  onColumnRevealedRef.current = onColumnRevealed;

  useEffect(() => {
    if (!api || !gridWrapperRef?.current) return undefined;

    const wrapper = gridWrapperRef.current;
    const flashTimers = new Set();
    const flashFrames = new Set();
    let scheduleFrame = 0;

    const getLabel = () => (tRef.current && tRef.current('scrollToColumn')) || 'Jump to column';
    const isApiValid = () => typeof api.isDestroyed === 'function' && !api.isDestroyed();

    // Custom tooltip rendered to <body> with fixed positioning, so it isn't clipped by the
    // tool panel's overflow and doesn't rely on the slow/suppressible native `title` tooltip.
    let tooltipEl = null;
    const getTooltipEl = () => {
      if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.className = TOOLTIP_CLASS;
        document.body.appendChild(tooltipEl);
      }
      return tooltipEl;
    };

    const showTooltip = (event) => {
      const tip = getTooltipEl();
      tip.textContent = getLabel();
      tip.style.visibility = 'hidden';
      tip.classList.add('is-visible');

      const btnRect = event.currentTarget.getBoundingClientRect();
      const tipRect = tip.getBoundingClientRect();

      let top = btnRect.top - tipRect.height - 6;
      if (top < 4) top = btnRect.bottom + 6; // flip below when there's no room above
      let left = btnRect.left + btnRect.width / 2 - tipRect.width / 2;
      left = Math.max(4, Math.min(left, window.innerWidth - tipRect.width - 4));

      tip.style.top = `${Math.round(top)}px`;
      tip.style.left = `${Math.round(left)}px`;
      tip.style.visibility = 'visible';
    };

    const hideTooltip = () => {
      if (tooltipEl) tooltipEl.classList.remove('is-visible');
    };

    // Columns shown in the tool panel: primary columns not suppressed from it, in grid order.
    const getPanelColumns = () =>
      (api.getColumns?.() || []).filter((col) => !col.getColDef?.()?.suppressColumnsToolPanel);

    const getColumnForRow = (row) => {
      if (!isApiValid()) return null;

      const label = row.querySelector(LABEL_SELECTOR)?.textContent?.trim();
      if (!label) return null;

      const byName = getPanelColumns().filter(
        (col) => (api.getDisplayNameForColumn?.(col, 'columnToolPanel') || '').trim() === label
      );
      if (byName.length === 0) return null;
      if (byName.length === 1) return byName[0];

      // Duplicate display names: prefer a visible match; never scroll to an unrelated column.
      return byName.find((col) => col.isVisible?.()) || byName[0];
    };

    const cssEscape = (value) => {
      try {
        return window.CSS?.escape ? window.CSS.escape(value) : value.replace(/"/g, '\\"');
      } catch (error) {
        return value;
      }
    };

    const flashColumn = (column) => {
      const colId = column.getColId?.();
      if (!colId) return;

      const selectorId = cssEscape(colId);
      let targets;
      try {
        targets = wrapper.querySelectorAll(`.ag-header-cell[col-id="${selectorId}"], .ag-cell[col-id="${selectorId}"]`);
      } catch (error) {
        return;
      }
      if (!targets.length) return;

      targets.forEach((el) => el.classList.remove(FLASH_CLASS));
      // Force a single reflow so re-adding the class restarts the animation on repeated clicks.
      void targets[0].offsetWidth;
      targets.forEach((el) => el.classList.add(FLASH_CLASS));

      const timer = setTimeout(() => {
        targets.forEach((el) => el.classList.remove(FLASH_CLASS));
        flashTimers.delete(timer);
      }, FLASH_DURATION);
      flashTimers.add(timer);
    };

    const handleLocateClick = (event) => {
      // Keep the native single-click show/hide toggle untouched.
      event.stopPropagation();
      event.preventDefault();

      const row = event.currentTarget.closest(ROW_SELECTOR);
      if (!row || !isApiValid()) return;

      const column = getColumnForRow(row);
      if (!column) return;

      try {
        // A hidden column can't be scrolled to — reveal it first so "locate" always does something.
        if (!column.isVisible?.()) {
          api.applyColumnState({ state: [{ colId: column.getColId(), hide: false }] });
          // Persist the reveal like the native checkbox does (applyColumnState uses source 'api',
          // which TableAgGrid's onColumnStateChanged intentionally skips).
          onColumnRevealedRef.current?.(api);
        }
        api.ensureColumnVisible(column, 'middle');
      } catch (error) {
        return;
      }

      // Let the horizontal scroll settle before flashing so freshly-rendered cells are included.
      const frame = requestAnimationFrame(() => {
        flashFrames.delete(frame);
        flashColumn(column);
      });
      flashFrames.add(frame);
    };

    const stopToggle = (event) => event.stopPropagation();

    const enhanceRow = (row) => {
      if (row.querySelector(`.${LOCATE_BTN_CLASS}`)) return;

      const button = document.createElement('span');
      button.className = LOCATE_BTN_CLASS;
      button.innerHTML = LOCATE_ICON_SVG;
      button.setAttribute('aria-label', getLabel());
      button.addEventListener('mousedown', stopToggle);
      button.addEventListener('click', handleLocateClick);
      button.addEventListener('mouseenter', showTooltip);
      button.addEventListener('mouseleave', hideTooltip);
      row.appendChild(button);
    };

    const enhanceAll = () => {
      if (!isApiValid()) return;
      const panel = wrapper.querySelector('.ag-column-panel');
      if (!panel) return;
      panel.querySelectorAll(ROW_SELECTOR).forEach(enhanceRow);
    };

    const scheduleEnhance = () => {
      if (scheduleFrame) return;
      scheduleFrame = requestAnimationFrame(() => {
        scheduleFrame = 0;
        enhanceAll();
      });
    };

    // Observe only the side bar (tool panel) rather than the whole grid, so grid-body scroll/cell
    // virtualization doesn't trigger the observer. Fall back to the wrapper if it isn't mounted yet.
    const observeTarget = wrapper.querySelector('.ag-side-bar') || wrapper;
    const observer = new MutationObserver(scheduleEnhance);
    observer.observe(observeTarget, { childList: true, subtree: true });
    enhanceAll();

    return () => {
      observer.disconnect();
      if (scheduleFrame) cancelAnimationFrame(scheduleFrame);
      flashFrames.forEach((frame) => cancelAnimationFrame(frame));
      flashFrames.clear();
      flashTimers.forEach((timer) => clearTimeout(timer));
      flashTimers.clear();
      wrapper.querySelectorAll(`.${LOCATE_BTN_CLASS}`).forEach((button) => {
        button.removeEventListener('mousedown', stopToggle);
        button.removeEventListener('click', handleLocateClick);
        button.removeEventListener('mouseenter', showTooltip);
        button.removeEventListener('mouseleave', hideTooltip);
        button.remove();
      });
      if (tooltipEl) {
        tooltipEl.remove();
        tooltipEl = null;
      }
    };
  }, [api, gridWrapperRef]);
};
